import type { PayableInvoice } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatInvoiceDate, formatPropertyLabel } from '../../lib/payments';

interface PayReviewProps {
  invoice: PayableInvoice;
  submitting: boolean;
  error: string | null;
  onConfirm: () => void;
  onBack: () => void;
}

/**
 * The deliberate one-invoice-at-a-time confirmation step (CHO-754). The
 * operator sees the exact amount and the property whose mapped card will be
 * charged before anything moves. (The service does not expose the card's last
 * four on the queue yet — CHO-753 follow-up — so they appear on the attempt
 * once initiated; there is deliberately no client-side workaround.) There is
 * deliberately no bulk path here.
 */
export function PayReview({ invoice, submitting, error, onConfirm, onBack }: PayReviewProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900">Confirm this payment</h2>
      <p className="text-sm text-gray-500 mt-1">
        One invoice at a time — after you start, you will approve the payment in your e-banking app.
      </p>

      <dl className="mt-5 space-y-3 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500">Property</dt>
          <dd className="font-medium text-gray-900 text-right">{formatPropertyLabel(invoice)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500">Biller</dt>
          <dd className="font-medium text-gray-900 text-right">{invoice.billerName ?? invoice.biller ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500">Invoice date</dt>
          <dd className="text-gray-900 text-right">{formatInvoiceDate(invoice.date)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500">Due date</dt>
          <dd className="text-gray-900 text-right">
            {invoice.dueDate ? (
              formatInvoiceDate(invoice.dueDate)
            ) : (
              // CHO-807: null means the source PDF states no due date — say so.
              <span className="text-gray-400 italic">None stated</span>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-t border-gray-100 pt-3">
          <dt className="text-gray-500">Amount</dt>
          <dd className="font-semibold text-gray-900 text-right text-base">
            {formatCurrency(invoice.amount, invoice.currency)}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-gray-500">Card to charge</dt>
          <dd className="font-medium text-gray-900 text-right">
            The card mapped to {formatPropertyLabel(invoice)}
            <span className="block text-xs font-normal text-gray-400">
              last four not exposed by the payment service yet — service-side follow-up to CHO-753; the attempt shows
              them once initiated
            </span>
          </dd>
        </div>
      </dl>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? 'Starting…' : 'Initiate payment'}
        </button>
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100"
        >
          Back to queue
        </button>
      </div>
    </div>
  );
}
